import { useEffect, useState } from 'react'
import { api, storeSession, type SignupInfo } from './api'

type Mode = 'signin' | 'signup'

/**
 * Sign-in and sign-up.
 *
 * Which one is offered depends on the deployment: a fresh install has no
 * accounts and opens straight into signup, an invite-only install shows a code
 * field, and an open one drops the code entirely.
 */
export default function Login({ onSignedIn }: { onSignedIn: () => void }): React.JSX.Element {
  const [info, setInfo] = useState<SignupInfo | null>(null)
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [invite, setInvite] = useState('')
  const [inviteNote, setInviteNote] = useState<string | null>(null)
  const [lockedEmail, setLockedEmail] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .signupInfo()
      .then((result) => {
        setInfo(result)
        // Nobody can sign in to an empty deployment, so do not offer it.
        if (result.firstRun) setMode('signup')
      })
      .catch((err: Error) => setError(err.message))
  }, [])

  /**
   * Validates the code as soon as it is complete, so an expired or already-used
   * invite is caught before the person picks a password.
   */
  useEffect(() => {
    const code = invite.trim()
    if (mode !== 'signup' || code.length < 14) {
      setInviteNote(null)
      setLockedEmail(false)
      return
    }
    let cancelled = false
    const id = setTimeout(() => {
      void api.checkInvite(code).then((result) => {
        if (cancelled) return
        if (result.valid) {
          setInviteNote(null)
          if (result.email) {
            // The invite is bound to one address — fill it in and lock it, so a
            // mismatch is impossible rather than merely rejected later.
            setEmail(result.email)
            setLockedEmail(true)
          }
        } else {
          setInviteNote(result.reason ?? 'this invite cannot be used')
          setLockedEmail(false)
        }
      })
    }, 350)
    return () => {
      cancelled = true
      clearTimeout(id)
    }
  }, [invite, mode])

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result =
        mode === 'signup'
          ? await api.signup(email, password, invite)
          : await api.login(email, password)
      storeSession(result.sessionToken)
      onSignedIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const signingUp = mode === 'signup'
  const needsInvite = Boolean(info && !info.firstRun && !info.openSignup)

  return (
    <div className="login-wrap">
      <form className="card login" onSubmit={(e) => void submit(e)}>
        <div className="brand">
          <span className="mark">
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M1 4.2A2.2 2.2 0 0 1 3.2 2h5.6A2.2 2.2 0 0 1 11 4.2v7.6A2.2 2.2 0 0 1 8.8 14H3.2A2.2 2.2 0 0 1 1 11.8V4.2Zm11 2.4 2.6-1.8a.6.6 0 0 1 .9.5v5.4a.6.6 0 0 1-.9.5L12 9.4V6.6Z" />
            </svg>
          </span>
          <h1>Smart Live Streamer</h1>
        </div>
        <p className="sub">
          {info?.firstRun
            ? 'No operator account exists yet — create the first one.'
            : signingUp
              ? 'Create an operator account.'
              : 'Sign in to manage your streaming fleet.'}
        </p>

        {signingUp && needsInvite && (
          <>
            <label>Invite code</label>
            <input
              required
              spellCheck={false}
              placeholder="K7QP-2M4X-9BTR"
              value={invite}
              onChange={(e) => setInvite(e.target.value.toUpperCase())}
            />
            {inviteNote && <p className="hint err">{inviteNote}</p>}
          </>
        )}

        <label>Email</label>
        <input
          type="email"
          required
          autoComplete="username"
          value={email}
          readOnly={lockedEmail}
          onChange={(e) => setEmail(e.target.value)}
        />
        {lockedEmail && <p className="hint">Set by your invite.</p>}

        <label>Password</label>
        <input
          type="password"
          required
          autoComplete={signingUp ? 'new-password' : 'current-password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {signingUp && <p className="hint">At least 10 characters.</p>}

        {error && <div className="banner error">{error}</div>}

        <button className="primary" type="submit" disabled={busy || info === null}>
          {busy ? 'Working…' : signingUp ? 'Create account' : 'Sign in'}
        </button>

        {/* An empty deployment has nothing to sign in to, so no switch is offered. */}
        {info && !info.firstRun && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setMode(signingUp ? 'signin' : 'signup')
              setError(null)
            }}
          >
            {signingUp ? 'Already have an account? Sign in' : 'Have an invite? Create an account'}
          </button>
        )}
      </form>
    </div>
  )
}
